import React, { useEffect } from "react";
import { useMerchantModal } from "./hooks/useMerchantModal";
import { useMerchantTableHandlers } from "./hooks/useMerchantTableHandlers";
import { useMerchantFormSubmit } from "./hooks/useMerchantFormSubmit";
import { useMobileMoneyModal } from "./hooks/useMobileMoneyModal";
import AddMobileMoneyModal from "../MyCompany/components/modals/AddMobileMoneyModal";
import MerchantTableContent from "./components/MerchantTableContent";
import MerchantFilters from "./components/MerchantFilters";
import { MerchantInstitutionModal } from "./modals/MerchantModals";
import useMerchantInstitutionForm from "./hooks/useMerchantForm";
import useMerchantReferenceData from "./hooks/useMerchantReferenceData";

const ManageMerchantContent = React.memo(({
  merchants,
  totalPages,
  totalElements,
  loadingData,
  isFetching,
  isError,
  onRetry,
  pagination,
  setPagination,
  searchQuery,
  segment,
  setSearchQuery,
  setSegment,
}) => {
  const merchantForm = useMerchantInstitutionForm();
  const { setMode, trigger } = merchantForm;

  const {
    modalMode,
    selectedMerchant,
    isModalOpen,
    openCreateModal,
    openEditModal,
    closeModal,
  } = useMerchantModal({ onModalClose: merchantForm.resetToDefaults });

  const {
    referenceData: {
      economicSectors,
      genders,
      productTransactionCodes,
      productOwnerships,
      productCategories,
      merchantSegments,
    },
    loadingStates: {
      economicSectorsLoading,
      gendersLoading,
      productTransactionCodesLoading,
      productOwnershipsLoading,
      productCategoriesLoading,
      merchantSegmentsLoading,
    },
    errorStates: {
      economicSectorsError: economicSectorsHasError,
      gendersError: gendersHasError,
      productTransactionCodesError: productTransactionCodesHasError,
      productOwnershipsError: productOwnershipsHasError,
      productCategoriesError: productCategoriesHasError,
      merchantSegmentsError: merchantSegmentsHasError,
    },
  } = useMerchantReferenceData(isModalOpen);

  useEffect(() => {
    if (modalMode) {
      setMode(modalMode);
      trigger();
    }
  }, [modalMode, setMode, trigger]);

  const { loadingIds, handleToggleStatus } = useMerchantTableHandlers();

  const hasActiveFilters = !!searchQuery || !!segment;
  const isTrueEmpty =
    !loadingData && !isError && merchants.length === 0 && !hasActiveFilters;
  const isFilteredEmpty =
    !loadingData && !isError && merchants.length === 0 && hasActiveFilters;

  const {
    handleSubmit,
    isSubmitting,
    merchantDetailsLoading,
    merchantDetailsError,
  } = useMerchantFormSubmit({
    merchantForm,
    modalMode,
    selectedMerchant,
    closeModal,
  });

  const {
    isOpen: mobileMoneyModalOpen,
    openModal: openMobileMoneyModal,
    closeModal: closeMobileMoneyModal,
    handleSubmit: handleMobileMoneySubmit,
    control: mobileFormControl,
    isCreating: creatingMerchantMobile,
    disableSubmit: disableMobileMoneySubmit,
    handleMsisdnBlur: handleMobileMoneyMsisdnBlur,
    handleMsisdnChange: handleMobileMoneyMsisdnChange,
    handleProviderChange: handleMobileMoneyProviderChange,
    isVerifying: isMobileMoneyVerifying,
    isVerified: isMobileMoneyVerified,
    isUnverified: isMobileMoneyUnverified,
    verificationMessage: mobileMoneyVerificationMessage,
  } = useMobileMoneyModal();

  return (
    <div className="flex flex-col min-h-full">
      <MerchantFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        segment={segment}
        onSegmentChange={setSegment}
        onAddMerchant={openCreateModal}
        disabled={loadingData || isTrueEmpty}
      />
      <div className="flex-1">
        <MerchantTableContent
          loadingData={loadingData}
          isFetching={isFetching}
          isError={isError}
          onRetry={onRetry}
          isTrueEmpty={isTrueEmpty}
          isFilteredEmpty={isFilteredEmpty}
          filteredMerchants={merchants}
          onEdit={openEditModal}
          onToggleStatus={handleToggleStatus}
          onMobileMoneyAdd={openMobileMoneyModal}
          loadingIds={loadingIds}
          pagination={pagination}
          totalPages={totalPages}
          totalElements={totalElements}
          onPaginationChange={setPagination}
        />
      </div>
      <MerchantInstitutionModal
        opened={!!modalMode}
        onClose={closeModal}
        mode={modalMode}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        economicSectorsData={economicSectors}
        economicSectorsLoading={economicSectorsLoading}
        gendersData={genders}
        gendersLoading={gendersLoading}
        productTransactionCodesData={productTransactionCodes}
        productTransactionCodesLoading={productTransactionCodesLoading}
        productOwnershipData={productOwnerships}
        productOwnershipLoading={productOwnershipsLoading}
        productCategoriesData={productCategories}
        productCategoriesLoading={productCategoriesLoading}
        merchantSegmentsData={merchantSegments}
        merchantSegmentsLoading={merchantSegmentsLoading}
        economicSectorsHasError={economicSectorsHasError}
        gendersHasError={gendersHasError}
        productTransactionCodesHasError={productTransactionCodesHasError}
        productOwnershipsHasError={productOwnershipsHasError}
        productCategoriesHasError={productCategoriesHasError}
        merchantSegmentsHasError={merchantSegmentsHasError}
        control={merchantForm.control}
        modeRef={merchantForm.modeRef}
        isFormValid={merchantForm.formState.isValid}
        disableSubmit={
          (modalMode === "edit" && merchantDetailsLoading) ||
          isSubmitting
        }
        merchantDetailsLoading={merchantDetailsLoading}
        merchantDetailsError={merchantDetailsError}
      />
      <AddMobileMoneyModal
        opened={mobileMoneyModalOpen}
        onClose={closeMobileMoneyModal}
        onSubmit={handleMobileMoneySubmit}
        control={mobileFormControl}
        isSubmitting={creatingMerchantMobile}
        disableSubmitBtn={disableMobileMoneySubmit}
        handleMsisdnBlur={handleMobileMoneyMsisdnBlur}
        handleMsisdnChange={handleMobileMoneyMsisdnChange}
        handleProviderChange={handleMobileMoneyProviderChange}
        isVerifying={isMobileMoneyVerifying}
        isVerified={isMobileMoneyVerified}
        isUnverified={isMobileMoneyUnverified}
        verificationMessage={mobileMoneyVerificationMessage}
      />
    </div>
  );
});

export default ManageMerchantContent;
